import React, { useRef, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import defaultAvatar from "../../assets/default-avatar.png";
import routePaths from "../../routes/routePaths";
import { useTranslation } from "react-i18next";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/firebaseConfig";
import { MdVerified, MdErrorOutline } from "react-icons/md";

const UserMenu = ({ user, avatar, userRoles = "", isMobile = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef();

  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate(routePaths.home);
  };

  if (!user) {
    return (
      <Link
        to={routePaths.login}
        className={`text-sm ${isMobile ? "px-2 py-1 ml-1" : "px-3 py-1"}
          border border-border rounded bg-card font-bold
          text-primary hover:bg-primary hover:text-white transition`}
      >
        {t("auth.login")}
      </Link>
    );
  }

  return (
    <div className="relative" ref={userMenuRef}>
      <img
        src={avatar || defaultAvatar}
        alt="User Avatar"
        className={`
          ${isMobile ? "h-8 w-8" : "h-9 w-9"}
          rounded border-2 border-primary cursor-pointer hover:border-secondary transition
        `}
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
      />
      {isUserMenuOpen && (
        <div
          className={`absolute right-0 mt-2 ${isMobile ? "w-48" : "w-56"}
            bg-card border border-primary rounded shadow z-50 animate-fadeIn`}
        >
          <div className="px-4 py-2 border-b border-border">
            <p className="text-sm font-semibold text-menu">{user.displayName || user.email}</p>
            <p className="text-xs flex items-center gap-1">
              {user.emailVerified ? (
                <>
                  <MdVerified className="inline text-green-500" />
                  <span className="text-green-600">{t("auth.verified")}</span>
                </>
              ) : (
                <>
                  <MdErrorOutline className="inline text-yellow-500" />
                  <span className="text-yellow-500">{t("auth.notVerified")}</span>
                </>
              )}
            </p>
          </div>
          <ul>
            <li>
              <Link
                to={routePaths.profile}
                className="block px-4 py-2 hover:bg-primary/10 text-menu transition"
                onClick={() => setIsUserMenuOpen(false)}
              >
                {t("auth.profile")}
              </Link>
              <Link
                to={routePaths.accountSettings}
                className="block px-4 py-2 hover:bg-primary/10 text-menu transition"
                onClick={() => setIsUserMenuOpen(false)}
              >
                {t("auth.settings")}
              </Link>

            {/* ✅ STUDENT MENU */}
            {userRoles.includes("student") && (
              <Link
                to={routePaths.studentAssignments || "/student/classes"}
                className="block px-4 py-2 hover:bg-blue-50 text-blue-600 font-semibold transition"
                onClick={() => setIsUserMenuOpen(false)}
              >
                📚 Lớp học của tôi
              </Link>
            )}

            {/* ✅ TEACHER MENU */}
            {userRoles.includes("teacher") && (
              <Link
                to={routePaths.teacherClasses || "/teacher/classes"}
                className="block px-4 py-2 hover:bg-purple-50 text-purple-600 font-semibold transition"
                onClick={() => setIsUserMenuOpen(false)}
              >
                🏫 Quản lí lớp học
              </Link>
            )}
            {userRoles.includes("parent") && (
              <Link
                to="/parent/children"
                className="block px-4 py-2 hover:bg-primary/10 text-primary font-semibold transition"
                onClick={() => setIsUserMenuOpen(false)}
              >
                Quản lý con
              </Link>
            )}
              {userRoles.includes("admin") && (
                <Link
                  to={routePaths.admin}
                  className="block px-4 py-2 hover:bg-secondary/10 text-primary font-bold transition"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  {t("auth.adminDashboard") || "Admin Dashboard"}
                </Link>
              )}
            </li>
            <li>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 hover:bg-orange/10 text-menu font-medium transition"
              >
                {t("auth.logout")}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
